import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Play, CheckCircle, FileText, Download, Users, AlertTriangle, Video, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";

interface TrainingVideo {
  id: string;
  title: string;
  description: string | null;
  video_path: string;
  thumbnail_path?: string | null;
  category: string | null;
  is_mandatory: boolean;
  mandatory_for_departments: string[];
  created_at: string;
  work_process_id: string | null;
}

interface TrainingVideoDocument {
  id: string;
  file_name: string;
  file_path: string;
  description: string | null;
}

interface TrainingVideoCardProps {
  video: TrainingVideo;
  documents: TrainingVideoDocument[];
  isCompleted: boolean;
  isMandatoryForUser: boolean;
  onWatch: (video: TrainingVideo) => void;
  onDownloadDocument: (doc: TrainingVideoDocument) => void;
  onEdit?: (video: TrainingVideo) => void;
  onDelete?: (video: TrainingVideo) => void;
  completionCount?: number;
  totalEmployees?: number;
  isAdmin?: boolean;
}

export function TrainingVideoCard({
  video,
  documents,
  isCompleted,
  isMandatoryForUser,
  onWatch,
  onDownloadDocument,
  onEdit,
  onDelete,
  completionCount = 0,
  totalEmployees = 0,
  isAdmin = false,
}: TrainingVideoCardProps) {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  useEffect(() => {
    if (video.thumbnail_path) {
      loadThumbnail();
    }
  }, [video.thumbnail_path]);

  const loadThumbnail = async () => {
    if (!video.thumbnail_path) return;
    try {
      const { data } = await supabase.storage
        .from('training-videos')
        .createSignedUrl(video.thumbnail_path, 3600);
      if (data?.signedUrl) {
        setThumbnailUrl(data.signedUrl);
      }
    } catch (error) {
      console.error('Error loading thumbnail:', error);
    }
  };

  return (
    <Card className={`group transition-shadow hover:shadow-md ${isCompleted ? 'border-primary/50 bg-primary/5' : ''}`}>
      {/* Thumbnail */}
      <div 
        className="relative aspect-video bg-muted overflow-hidden cursor-pointer"
        onClick={() => onWatch(video)}
      >
        {thumbnailUrl ? (
          <img 
            src={thumbnailUrl} 
            alt={video.title}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted-foreground/10">
            <Video className="h-12 w-12 text-muted-foreground/50" />
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-14 h-14 rounded-full bg-primary/90 flex items-center justify-center">
            <Play className="h-6 w-6 text-primary-foreground ml-1" />
          </div>
        </div>
        {isCompleted && (
          <div className="absolute top-2 left-2 bg-primary text-primary-foreground rounded-full p-1">
            <CheckCircle className="h-4 w-4" />
          </div>
        )}
        {isMandatoryForUser && !isCompleted && (
          <div className="absolute top-2 left-2 bg-destructive text-destructive-foreground rounded-full p-1">
            <AlertTriangle className="h-4 w-4" />
          </div>
        )}
      </div>

      <CardHeader className="pb-2 pt-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <CardTitle className="text-base line-clamp-1">{video.title}</CardTitle>
            {video.description && (
              <CardDescription className="line-clamp-2 text-sm">{video.description}</CardDescription>
            )}
          </div>
          <div className="flex items-start gap-1">
            <div className="flex flex-col gap-1">
              {video.is_mandatory && (
                <Badge variant="destructive" className="text-xs">Pflicht</Badge>
              )}
              {video.category && (
                <Badge variant="secondary" className="text-xs">{video.category}</Badge>
              )}
            </div>
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 -mr-2">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit?.(video)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Bearbeiten
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => onDelete?.(video)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Löschen
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Admin: Completion Stats */}
        {isAdmin && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            <span>{completionCount} von {totalEmployees} haben bestätigt</span>
            {totalEmployees > 0 && (
              <span className="text-xs">
                ({Math.round((completionCount / totalEmployees) * 100)}%)
              </span>
            )}
          </div>
        )}

        {/* Watch Button */}
        <Button 
          onClick={() => onWatch(video)} 
          className="w-full gap-2"
          variant={isCompleted ? "outline" : "default"}
        >
          <Play className="h-4 w-4" />
          {isCompleted ? "Erneut ansehen" : "Video ansehen"}
        </Button>

        {/* Attached Documents */}
        {documents.length > 0 && (
          <div className="border-t pt-3 space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Angehängte Dokumente:</p>
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center gap-2 text-sm">
                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="flex-1 truncate">{doc.file_name}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => onDownloadDocument(doc)}
                >
                  <Download className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Hinzugefügt: {format(new Date(video.created_at), 'dd.MM.yyyy', { locale: de })}
        </p>
      </CardContent>
    </Card>
  );
}
